import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LooksPage() {
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter") || "all";
  const [filter, setFilter] = useState(initialFilter);
  // Phase 1 menswear foundation — primary audience chip driving the /ai/looks
  // ?audience= query. 'all' (default) keeps existing behaviour. 'her' includes
  // women + unisex only. 'him' includes men + unisex only. 'two' shows only
  // Looks for Two (mixed men + women).
  const initialAudience = searchParams.get("audience") || "all";
  const [audience, setAudience] = useState(initialAudience);

  useEffect(() => {
    setLoading(true);
    const url = audience === "all" ? `${API}/ai/looks` : `${API}/ai/looks?audience=${audience}`;
    fetch(url)
      .then(r => r.json())
      .then(d => setLooks(d.looks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [audience]);

  const occasions = [...new Set(looks.map(l => l.occasion).filter(Boolean))];
  const lookTypes = [...new Set(looks.map(l => l.look_type).filter(Boolean))];
  const allTags = [...new Set([...occasions, ...lookTypes])];
  const filtered = filter === "all" ? looks : looks.filter(l => l.occasion === filter || l.look_type === filter);

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        title="Curated Looks - Designer Outfit Combinations"
        description="Browse our AI-curated outfit combinations. Complete styled looks for weddings, festivals & events. Rent the full look or pick individual pieces in Melbourne."
        path="/looks"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Curated Looks" }]}
      />
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 pt-8 pb-16">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl text-[#282828] tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Curated Looks
          </h1>
          <p className="text-sm text-[#676664] mt-2 max-w-md mx-auto">
            Complete outfit inspirations styled by AI. Rent the full look or pick individual pieces.
          </p>
        </div>

        {/* Primary audience filter — Phase 1 menswear foundation */}
        <div className="flex flex-wrap justify-center gap-2 mb-3" data-testid="look-audience-chips">
          {[
            { key: "all", label: "All" },
            { key: "her", label: "For Her" },
            { key: "him", label: "For Him" },
            { key: "two", label: "Looks for Two" },
          ].map(a => (
            <button
              key={a.key}
              onClick={() => { setAudience(a.key); setFilter("all"); }}
              data-testid={`look-audience-${a.key}`}
              className={`px-5 py-1.5 text-xs tracking-wider uppercase border transition-colors ${audience === a.key ? "bg-[#9A7B4F] text-white border-[#9A7B4F]" : "border-[#9A7B4F]/40 text-[#9A7B4F] hover:bg-[#9A7B4F]/10"}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* Occasion Filters */}
        {allTags.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="look-filters">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-1.5 text-xs tracking-wider uppercase border transition-colors ${filter === "all" ? "bg-[#282828] text-white border-[#282828]" : "border-[#282828]/15 text-[#676664] hover:border-[#282828]/40"}`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setFilter(tag)}
                className={`px-4 py-1.5 text-xs tracking-wider uppercase border transition-colors ${filter === tag ? "bg-[#282828] text-white border-[#282828]" : "border-[#282828]/15 text-[#676664] hover:border-[#282828]/40"}`}
              >
                {tag}
              </button>
            ))}
            <Link
              to="/build-your-look"
              className="px-4 py-1.5 text-xs tracking-wider uppercase border border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F] hover:text-white transition-colors flex items-center gap-1.5"
              data-testid="build-your-look-btn"
            >
              <Sparkles className="w-3 h-3" />
              Build Your Own
            </Link>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white animate-pulse">
                <div className="aspect-[4/5] bg-[#E8E0D5]" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-[#E8E0D5] rounded w-2/3" />
                  <div className="h-3 bg-[#E8E0D5] rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Looks Grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="looks-grid">
            {filtered.map(look => (
              <LookCard key={look.id} look={look} />
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20" data-testid="looks-empty">
            {audience === "him" && (
              <p className="text-[#676664] text-sm">
                <strong className="block text-[#282828] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem" }}>Menswear is coming soon.</strong>
                We're curating the first For Him looks — check back shortly.
              </p>
            )}
            {audience === "two" && (
              <p className="text-[#676664] text-sm">
                <strong className="block text-[#282828] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem" }}>Looks for Two is coming soon.</strong>
                Coordinated two-person combinations for couples, siblings and friends will be here shortly.
              </p>
            )}
            {audience !== "him" && audience !== "two" && (
              <p className="text-[#676664] text-sm">No looks available yet. Check back soon!</p>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function LookCard({ look }) {
  // Outfit categories that should always be the hero (left) image
  const OUTFIT_CATS = ['lehengas', 'sarees', 'anarkalis & shararas', 'indo western', 'indo-western'];
  const isOutfit = (item) => OUTFIT_CATS.includes((item.category || '').toLowerCase());

  // Hero = outfit item first, fallback to hero_product_id, then items[0]
  const heroItem = look.items?.find(i => isOutfit(i))
    || look.items?.find(i => i.id === look.hero_product_id)
    || look.items?.[0];
  // Accessories = everything except the outfit item (right side boxes)
  const otherItems = look.items?.filter(i => i.id !== heroItem?.id) || [];
  const itemCount = look.items?.length || 0;
  const tierDiscount = itemCount >= 4 ? 20 : itemCount === 3 ? 15 : itemCount === 2 ? 10 : 0;
  const discountedPrice = tierDiscount > 0 ? Math.round(look.total_price_4_day * (1 - tierDiscount / 100)) : look.total_price_4_day;

  // Build product map for custom layout
  const itemMap = {};
  for (const item of look.items || []) itemMap[item.id] = item;

  // Check if this look has a custom layout with actual products assigned
  const hasCustomLayout = look.layout && look.layout.boxes && look.layout.boxes.length > 0 && look.layout.boxes.some(b => b.product_id && itemMap[b.product_id]);

  return (
    <Link to={`/looks/${look.id}`} className="group bg-white border border-[#282828]/5 hover:border-[#9A7B4F]/20 transition-all overflow-hidden" data-testid={`look-card-${look.id}`}>
      {/* Image Section */}
      <div className="aspect-[4/5] bg-[#f5f3f0] overflow-hidden relative">
        {hasCustomLayout ? (
          /* Custom Layout Rendering */
          <div className="w-full h-full" style={{
            display: "grid",
            gridTemplateColumns: `repeat(${look.layout.columns || 3}, 1fr)`,
            gridTemplateRows: `repeat(${Math.max(...look.layout.boxes.map(b => b.row + b.rowSpan), 1)}, 1fr)`,
            gap: "1px",
          }}>
            {look.layout.boxes.map(box => {
              const item = itemMap[box.product_id];
              return (
                <div key={box.id} style={{
                  gridColumn: `${box.col + 1} / span ${box.colSpan}`,
                  gridRow: `${box.row + 1} / span ${box.rowSpan}`,
                }} className="overflow-hidden bg-[#E8E0D5]">
                  {item?.image ? (
                    <img src={item.image} alt={item.name || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[8px] text-[#676664]/30">{box.label || ""}</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Default 70/30 Layout */
          <div className="flex h-full">
            {/* LEFT: Outfit image */}
            <div className={`${otherItems.length > 0 ? 'w-[70%]' : 'w-full'} h-full overflow-hidden relative`}>
              {heroItem?.image ? (
                <img src={heroItem.image} alt={look.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#676664]/30 text-sm">No Image</div>
              )}
            </div>
            {/* RIGHT: Accessories only */}
            {otherItems.length > 0 && (
              <div className="w-[30%] flex flex-col">
                {otherItems.slice(0, 3).map((item) => (
                  <div key={item.id} className="flex-1 overflow-hidden border-l border-b border-[#f5f3f0] last:border-b-0">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-[#E8E0D5] flex items-center justify-center">
                        <span className="text-[8px] text-[#676664]/40">{item.category}</span>
                      </div>
                    )}
                  </div>
                ))}
                {otherItems.length > 3 && (
                  <div className="flex-1 bg-[#282828] flex items-center justify-center border-l border-[#f5f3f0]">
                    <span className="text-white text-[10px]">+{otherItems.length - 3}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Occasion badge */}
        {(look.occasion || look.look_type) && (
          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1">
            <span className="text-[10px] tracking-wider uppercase text-[#676664]">{look.look_type || look.occasion}</span>
          </div>
        )}
        {/* Tier discount badge */}
        {tierDiscount > 0 && (
          <div className="absolute top-3 right-3 bg-[#9A7B4F] px-2.5 py-1">
            <span className="text-[10px] tracking-wider uppercase text-white font-medium">Save {tierDiscount}%</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="text-[15px] text-[#282828] group-hover:text-[#9A7B4F] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
          {look.name}
        </h3>
        {/* Items list */}
        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-1.5">
          {look.items?.map((item, i) => (
            <span key={item.id} className="text-[10px] text-[#676664]">
              {item.name}{i < look.items.length - 1 ? " +" : ""}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#282828]/5">
          <div>
            <span className="text-[12px] text-[#9A7B4F] font-medium">
              {itemCount} pieces
              {tierDiscount > 0 ? (
                <>
                  {" · "}<span className="line-through opacity-50">${look.total_price_4_day}</span>{" "}
                  <span className="font-semibold">${discountedPrice}</span>
                </>
              ) : (
                <> · From ${look.total_price_4_day}</>
              )}
            </span>
          </div>
          <span className="text-[11px] text-[#676664] group-hover:text-[#9A7B4F] flex items-center gap-1 transition-colors">
            View Look <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}
